import {
  trackDistributionChannelDetailViewed,
  trackSettingsDistributionChannelDensityChanged,
  trackSettingsDistributionChannelFiltered,
  trackSettingsDistributionChannelViewModeChanged,
} from '@src/analytics/EventTracker';
import styled, { css } from 'styled-components';

import AddChannelPicker from './AddChannelPicker';
import ChannelComparisonTable from './ChannelComparisonTable';
import ChannelIcon from './ChannelIcon';
import DistributionChannelRow from './DistributionChannelRow';

import type { DistributionChannelsState } from '@src/stores-v2/DistributionChannelsStore';
import type { DistributionChannel } from '@src/types.ts/distribution-channel';
import type { Dispatch, SetStateAction } from 'react';

type ViewMode = 'list' | 'comparison';
type Density = 'summary' | 'details';

export interface BodyProps {
  loadState: DistributionChannelsState;
  retry: () => void;
  selectedIds: Set<string>;
  setSelectedIds: (next: Set<string>) => void;
  viewMode: ViewMode;
  setViewMode: (next: ViewMode) => void;
  density: Density;
  setDensity: (next: Density) => void;
  showcaseId: string | undefined;
  enabledIds: Set<string>;
  pickerOpen: boolean;
  setPickerOpen: Dispatch<SetStateAction<boolean>>;
  onAdd: (channel: DistributionChannel) => void;
  onRemove: (channel: DistributionChannel) => void;
  busyChannelId: string | null;
}

const DistributionChannelsSectionBody = ({
  loadState,
  retry,
  selectedIds,
  setSelectedIds,
  viewMode,
  setViewMode,
  density,
  setDensity,
  showcaseId,
  enabledIds,
  pickerOpen,
  setPickerOpen,
  onAdd,
  onRemove,
  busyChannelId,
}: BodyProps) => {
  // Show cached channels (if any) while a refresh is in flight rather than
  // flashing the loading state on remount.
  if (
    (loadState.status === 'idle' || loadState.status === 'loading') &&
    loadState.channels.length === 0
  ) {
    return <Muted>Loading channels…</Muted>;
  }
  if (loadState.status === 'error' && loadState.channels.length === 0) {
    return (
      <ErrorRow>
        <span>{loadState.error ?? "Couldn't load distribution channels."}</span>
        <button type="button" onClick={retry}>
          Retry
        </button>
      </ErrorRow>
    );
  }

  const allChannels = loadState.channels;
  const isEmpty = allChannels.length === 0;
  const displayedChannels =
    selectedIds.size === 0
      ? allChannels
      : allChannels.filter(c => selectedIds.has(c.id));

  const applyFilter = (next: Set<string>) => {
    setSelectedIds(next);
    const slugs = allChannels.filter(c => next.has(c.id)).map(c => c.slug);
    trackSettingsDistributionChannelFiltered({
      selected_channel_count: next.size,
      total_channel_count: allChannels.length,
      selected_channel_slugs: slugs,
    });
  };

  const toggle = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    applyFilter(next);
  };

  const allSelected = selectedIds.size === allChannels.length;

  const changeViewMode = (next: ViewMode) => {
    if (next === viewMode) return;
    setViewMode(next);
    trackSettingsDistributionChannelViewModeChanged({ view_mode: next });
  };

  const changeDensity = (next: Density) => {
    if (next === density) return;
    setDensity(next);
    trackSettingsDistributionChannelDensityChanged({ density: next });
    if (next === 'details') {
      trackDistributionChannelDetailViewed();
    }
  };

  return (
    <>
      <Toolbar>
        <IconStrip>
          {allChannels.map(channel => {
            const isActive = selectedIds.has(channel.id);
            return (
              <IconChip
                key={channel.id}
                type="button"
                $active={isActive}
                aria-pressed={isActive}
                onClick={() => toggle(channel.id)}
              >
                <ChannelIcon
                  iconUrl={channel.iconUrl}
                  alt={`${channel.title} icon`}
                  size={20}
                  variant="plain"
                  padInside={false}
                />
                <IconTooltip role="tooltip">{channel.title}</IconTooltip>
              </IconChip>
            );
          })}
        </IconStrip>
        <RightControls>
          {showcaseId && (
            <AddWrapper>
              <AddButton
                type="button"
                onClick={() => setPickerOpen(o => !o)}
                aria-expanded={pickerOpen}
              >
                + Add channel
              </AddButton>
              {pickerOpen && (
                <AddChannelPicker
                  showcaseId={showcaseId}
                  enabledIds={enabledIds}
                  onPick={onAdd}
                  onClose={() => setPickerOpen(false)}
                />
              )}
            </AddWrapper>
          )}
          <TextButton
            type="button"
            onClick={() =>
              applyFilter(
                allSelected ? new Set() : new Set(allChannels.map(c => c.id)),
              )
            }
            disabled={isEmpty}
          >
            {allSelected ? 'Unselect All' : 'Select All'}
          </TextButton>
          <Segmented role="tablist" aria-label="Density">
            <SegmentedButton
              type="button"
              role="tab"
              aria-selected={density === 'summary'}
              $active={density === 'summary'}
              onClick={() => changeDensity('summary')}
            >
              Summary
            </SegmentedButton>
            <SegmentedButton
              type="button"
              role="tab"
              aria-selected={density === 'details'}
              $active={density === 'details'}
              onClick={() => changeDensity('details')}
            >
              Details
            </SegmentedButton>
          </Segmented>
          <Segmented role="tablist" aria-label="View mode">
            <SegmentedButton
              type="button"
              role="tab"
              aria-selected={viewMode === 'list'}
              $active={viewMode === 'list'}
              onClick={() => changeViewMode('list')}
            >
              List
            </SegmentedButton>
            <SegmentedButton
              type="button"
              role="tab"
              aria-selected={viewMode === 'comparison'}
              $active={viewMode === 'comparison'}
              onClick={() => changeViewMode('comparison')}
            >
              Compare
            </SegmentedButton>
          </Segmented>
        </RightControls>
      </Toolbar>
      {isEmpty ? (
        <Muted>No channels configured yet.</Muted>
      ) : viewMode === 'list' ? (
        <List>
          {displayedChannels.map(channel => (
            <DistributionChannelRow
              key={channel.id}
              channel={channel}
              onRemove={() => onRemove(channel)}
              removeDisabled={busyChannelId === channel.id}
              variant={density}
            />
          ))}
        </List>
      ) : (
        <ChannelComparisonTable
          channels={displayedChannels}
          variant={density}
        />
      )}
    </>
  );
};

export default DistributionChannelsSectionBody;

const Muted = styled.div`
  font-size: var(--text-sm);
  color: var(--text-secondary);
  padding: 24px 0;
`;

const ErrorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 0;
  font-size: var(--text-sm);
  color: var(--text-secondary);

  > button {
    background: transparent;
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 4px 12px;
    color: var(--foreground);
    cursor: pointer;

    &:hover {
      background: var(--surface-elevated);
    }
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 8px 0 16px;
`;

const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TextButton = styled.button`
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 6px 12px;
  font-size: var(--text-sm);
  color: var(--foreground);
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--surface-elevated);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AddWrapper = styled.div`
  position: relative;
`;

const AddButton = styled.button`
  background: var(--accent);
  color: var(--accent-foreground, #fff);
  border: 1px solid var(--accent);
  border-radius: 6px;
  padding: 6px 12px;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;

  &:hover {
    filter: brightness(1.05);
  }
`;

const Segmented = styled.div`
  display: inline-flex;
  border: 1px solid var(--border);
  border-radius: 6px;
  overflow: hidden;
`;

const SegmentedButton = styled.button<{ $active: boolean }>`
  background: ${p => (p.$active ? 'var(--surface-elevated)' : 'transparent')};
  border: none;
  padding: 6px 12px;
  font-size: var(--text-sm);
  color: ${p => (p.$active ? 'var(--foreground)' : 'var(--text-secondary)')};
  cursor: pointer;

  & + & {
    border-left: 1px solid var(--border);
  }

  &:hover {
    color: var(--foreground);
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  > * + * {
    border-top: 1px solid var(--border);
  }
`;

const IconTooltip = styled.span`
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: var(--popover);
  color: var(--popover-foreground);
  font-size: 12px;
  font-weight: var(--font-weight-medium);
  padding: 4px 8px;
  border-radius: 6px;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease;
  z-index: 10;
  border: 1px solid var(--border-subtle);
  box-shadow: var(--elevation-sm);
`;

const IconStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

// Mirrors the ClipProducer's SocialButton treatment (see
// src/components/common/SocialClipButtonList.tsx) — bordered square that
// toggles to an accent fill when active. Acts as a multi-select filter for
// the channel list / comparison view below.
const IconChip = styled.button<{ $active: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 1px;
  border-radius: 8px;
  cursor: pointer;
  box-sizing: border-box;
  transition:
    color var(--transition-fast) ease,
    border-color var(--transition-fast) ease,
    background var(--transition-fast) ease;

  ${p =>
    p.$active
      ? css`
          background: var(--accent);
          border: 1px solid var(--accent);

          img {
            filter: brightness(0);
          }
        `
      : css`
          background: var(--text-muted);
          border: 1px solid var(--text-muted);

          img {
            filter: brightness(0);
          }

          &:hover {
            background: var(--accent);
            border-color: var(--accent);
          }
        `}

  &:hover ${IconTooltip} {
    opacity: 1;
  }
`;
